/**
 * @file svelte 组件注册和加载器
 *
 * 提供异步和同步注册 svelte 组件的方法，加载 svelte 组件的方法
 * 对标 vue 的组件注册和加载 `Vue.component`
 */

import type { ComponentType, SvelteComponent } from 'svelte';

/**
 * 异步注册 svelte 组件
 *
 * @param name 组件名
 * @param compLoader 组件加载函数
 */
export const registerSvelteComponent = (name: string, compLoader: ISvelteCompLoader) => {
  if (!componentMap[name]) {
    componentMap[name] = {
      loader: compLoader,
      loadStatus: ELoadStatus.init,
    };
  }
};

/**
 * 同步注册 svelte 组件
 *
 * @param name 组件名
 * @param svelteComponent 组件类
 */
export const registerSvelteComponentSync = (name: string, svelteComponent: ComponentType<SvelteComponent>, override = false) => {
  if (!componentMap[name] || override) {
    componentMap[name] = {
      loader: () => Promise.resolve(svelteComponent),
      loadStatus: ELoadStatus.loaded,
      comp: svelteComponent,
    };
  }
};

/**
 * 解析 svelte 组件
 *
 * @param name 组件名
 * @returns svelte 组件
 */
export const resolveSvelteComponent: IResolveComponent = async (name: string) => new Promise((resolve, reject) => {
  const comp = componentMap[name];

  if (!comp) {
    console.error('[sca] comp resolve error, not register', name);
    reject(`svelte 组件[${name}]解析失败，未注册`);
    return;
  }

  if (comp.loadStatus === ELoadStatus.init) {
    comp.resolveList = comp.resolveList || [];
    comp.rejectList = comp.rejectList || [];

    comp.loader().then((res: ComponentType<SvelteComponent>) => {
      comp.loadStatus = ELoadStatus.loaded;
      comp.comp = res;
      resolve(res);
      comp.resolveList?.forEach((resolveCallback) => {
        resolveCallback(res);
      });
      comp.rejectList = [];
    }, (error: Error) => {
      const errorMessage = `[sca] comp load error ${name}`;
      console.error(errorMessage, error);
      comp.loadStatus = ELoadStatus.error;
      comp.errorMessage = errorMessage;
      comp.rejectList?.forEach(() => {
        reject(error.message);
      });
      comp.resolveList = [];
    });

    comp.loadStatus = ELoadStatus.loading;
    return;
  }

  if (comp.loadStatus === ELoadStatus.loading) {
    console.log('[sca] comp loading', name);
    comp.resolveList?.push(resolve);
    comp.rejectList?.push(reject);
    return;
  }

  if (comp.loadStatus === ELoadStatus.loaded) {
    console.log('[sca] comp loaded', name);
    resolve(comp.comp!);
    return;
  }

  reject(comp.errorMessage!);
});

/**
 * 加载状态
 */
enum ELoadStatus {
  /**
   * 初始化
   */
  init = 'init',
  /**
   * 加载中
   */
  loading = 'loading',
  /**
   * 加载错误
   */
  error = 'error',
  /**
   * 已加载
   */
  loaded = 'loaded'
}

type ISvelteCompLoader = () => Promise<ComponentType<SvelteComponent>>;
type IResolveComponent = (name: string) => Promise<ComponentType<SvelteComponent>>;

const componentMap: Record<string, {
  loader: ISvelteCompLoader,
  loadStatus: ELoadStatus,
  comp?: ComponentType<SvelteComponent>;
  errorMessage?: string;
  resolveList?: Array<(comp: ComponentType<SvelteComponent>) => void>;
  rejectList?: Array<(reason: string) => void>;
}> = {};
